import { Link } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AutopilotTaskDto, useAssignTaskGroupToUser } from '@urbanx/agx.activities.client';
import { ContentLayout, PageLayout } from 'layout';
import { useContext, useEffect, useRef, useState } from 'react';
import './allTasks.scss';
import {
  AgxColumn,
  AgxRow,
  AgxTable,
  Icon_Clock,
  type IColumnConfig,
  type RowData,
} from '@urbanx/agx.ui-components2';
import useTaskSearch from './useTaskSearch';
import { useTaskCollectionChangeNotifier } from './useTaskCollectionChangeNotifier';
import TaskFilters from './TaskFilters/TaskFilters';
import { TaskFilterState } from './TaskFilters/TaskFiltersState';
import { useGetAssigneeOptions } from './TaskFilters/useGetAssigneeOptions';
import { getTaskLink } from 'utils/tasks/useNavigateToTask';
import { getTaskDashboardLabel, getTaskDisplayGroups } from 'utils/tasks';
import { TaskCell } from './Cells/TaskCell';
import TaskAgencyCell from './Cells/TaskAgencyCell';
import TaskCampaignCell from './Cells/TaskCampaignCell';
import TaskAssigneeCell from './Cells/TaskAssigneeCell';
import TaskStatusCell from './Cells/TaskStatusCell';
import TaskDueCell from './Cells/TaskDueCell';
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError';
import { useAgencyFilter } from 'hooks/useAgencyFilter';

const columnConfig: IColumnConfig[] = [
  { name: 'Task Type', width: '24%' },
  { name: 'Agency', width: '18%' },
  { name: 'Campaign', width: '25%' },
  {
    name: 'Assignee',
    disableRowLink: true,
    width: '15%',
    align: 'center',
    onClick: (event) => event.stopPropagation(),
  },
  { name: 'Status', width: '10%' },
  { name: 'Due', width: '8%', align: 'right' },
];

const TASK_ROW_HEIGHT = 72;
const TASK_ROW_OVERSCAN = 8;

const TasksTable = () => {
  const [filters, setFilters] = useState<TaskFilterState | undefined>(undefined);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const { userAccount } = useContext(AuthContext);

  const { assigneeOptions, assigneeOptionsPending } = useGetAssigneeOptions(
    filters?.assignedToUserIds
  );
  const {
    agencyOptions,
    effectiveAgencyIds,
    agencies,
    isPending: isAgencyFilterPending,
    isAgencyScopeUnavailable,
    hasNoMatchingAgencies,
  } = useAgencyFilter({ selectedValues: filters?.agencyIds });

  const effectiveFilters = filters ? { ...filters, agencyIds: effectiveAgencyIds } : undefined;
  const filtersPending = isAgencyFilterPending || assigneeOptionsPending;

  const {
    hasMore,
    totalCount,
    tasks,
    searchReady,
    searchAllTasksPending,
    loadMore,
    refreshSearch,
    refreshSearchInBackground,
  } = useTaskSearch(
    effectiveFilters,
    filtersPending,
    hasNoMatchingAgencies,
    isAgencyScopeUnavailable
  );

  useTaskCollectionChangeNotifier({
    filters: effectiveFilters,
    baselineReady: searchReady,
    refreshTasks: refreshSearch,
  });
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();

  const taskGroups = getTaskDisplayGroups(tasks);
  const rows = taskGroups.map((group) => group.displayTask);
  const userName = [userAccount?.firstName, userAccount?.lastName]
    .filter((namePart) => namePart != null && namePart.length > 0)
    .join(' ');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !searchAllTasksPending) {
          loadMore();
        }
      },
      { threshold: 1 }
    );
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasMore, loadMore, searchAllTasksPending]);

  const { assignTaskGroupToUser, assignTaskGroupToUserPending } = useAssignTaskGroupToUser({
    onSuccess: () => {
      void refreshSearchInBackground();
    },
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return;

      void handleWorkflowConcurrencyError(error, assignmentWorkflowRef.current, {
        refresh: refreshSearchInBackground,
      });
    },
  });

  const onAssignTaskToUser = async (userId?: string, taskId?: string) => {
    if (!userId || !taskId || assignTaskGroupToUserPending) return;

    const taskGroup = taskGroups.find((group) => group.displayTask.id === taskId);
    if (!taskGroup) return;

    assignmentWorkflowRef.current = taskGroup.displayTask;

    await assignTaskGroupToUser({ taskId, userId });
  };

  const agenciesForRows = agencies;
  const tableRows: RowData[] = rows.map((task, rowIdx) => ({
    dataTestId: task.id,
    'Task Type': <TaskCell task={task} index={rowIdx} isNew={!!task.isNew} />,
    Agency: (
      <TaskAgencyCell task={task} agencies={agenciesForRows} index={rowIdx} isNew={!!task.isNew} />
    ),
    Campaign: (
      <TaskCampaignCell
        propertyAddress={task.propertyAddress}
        index={rowIdx}
        isNew={!!task.isNew}
      />
    ),
    Assignee: (
      <TaskAssigneeCell
        task={task}
        assigneeOptions={assigneeOptions}
        onAssignTask={onAssignTaskToUser}
      />
    ),
    Status: <TaskStatusCell task={task} />,
    Due: <TaskDueCell task={task} />,
  }));

  return (
    <PageLayout
      agentName={userName}
      primaryPageTitle="All Tasks"
      pageIcon={
        <div className="iconContainer">
          <Icon_Clock height={28} width={28} color="var(--fg-white)" />
        </div>
      }
      header={
        <TaskFilters
          totalCount={totalCount}
          onFilterChange={setFilters}
          pending={assigneeOptionsPending || isAgencyFilterPending}
          agencyOptions={agencyOptions}
          assigneeOptions={assigneeOptions}
        />
      }
    >
      <ContentLayout>
        <AgxRow fill>
          <AgxColumn fill gap="spacing-xl">
            <div className="allTasksTable">
              <AgxTable
                columns={columnConfig}
                rows={tableRows}
                variant={tableRows.length ? 'default' : 'minimal'}
                showLoadingState={
                  (searchAllTasksPending || totalCount === undefined) && tableRows.length === 0
                }
                tableStyle="square"
                noDataLabel="No task found."
                noDataGlyphType="checklistSmile"
                virtualized={{
                  rowHeight: TASK_ROW_HEIGHT,
                  overscanRows: TASK_ROW_OVERSCAN,
                }}
                renderRowLink={(rowIndex, linkProps) => {
                  const task = rows[rowIndex];
                  const to = getTaskLink(task);
                  return to ? (
                    <Link
                      {...linkProps}
                      to={to}
                      aria-label={
                        getTaskDashboardLabel(task).trim() ||
                        task.displayTitle?.trim() ||
                        (task.id ? `Task ${task.id}` : 'Open task')
                      }
                    />
                  ) : undefined;
                }}
              />
            </div>
            {/* Loader indicator */}
            <div ref={loaderRef} style={{ textAlign: 'center', padding: 16 }}>
              {searchAllTasksPending ? 'Loading...' : ''}
            </div>
          </AgxColumn>
        </AgxRow>
      </ContentLayout>
    </PageLayout>
  );
};

export default TasksTable;
